import React from 'react'
import { useIntl } from 'react-intl'
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material'
import { StudentRatingState } from '../../../../features/types'
import { ratingApiSlice } from '../../../../features/rating/ratingSlice'
import { tableStyles, ui, tone } from '../../../../utils/uiTokens'

interface Props {
    monthNumber: number
    groupId?: number
    courseSubjectId?: number
    filter?: string
}

const WEEKS = [1, 2, 3, 4]
const WEEK_MAX = 40
const MONTH_MAX = 160
const TOTAL_SLOTS = 8 // 4 недели * 2 практики
const PRESENT = new Set(['WITH_CAMERA', 'WITHOUT_CAMERA'])

// Значок практики: К камерамен, Б камерасыз, — болмаған, ? белгіленбеген
const BADGE: Record<string, { letter: string; bg: string; color: string }> = {
    WITH_CAMERA: { letter: 'К', bg: tone.ok.bg, color: tone.ok.text },
    WITHOUT_CAMERA: { letter: 'Б', bg: tone.warn.bg, color: tone.warn.text },
    ABSENT: { letter: '—', bg: tone.bad.bg, color: tone.bad.text },
    '': { letter: '?', bg: tone.neutral.bg, color: ui.muted },
}

type WeekCell = { p1: string; p2: string; point: number }

const Badge: React.FC<{ status?: string }> = ({ status }) => {
    const b = BADGE[status || ''] || BADGE['']
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 26,
                height: 26,
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 600,
                background: b.bg,
                color: b.color,
            }}
        >
            {b.letter}
        </Box>
    )
}

const AllWeeksSummary: React.FC<Props> = ({ monthNumber, groupId, filter }) => {
    const { formatMessage } = useIntl()

    const base = { groupId: groupId || 0, monthNumber, filter: filter || '', page: 0, size: 200 }
    const q1 = ratingApiSlice.useGetStudentRatingQuery({ ...base, weekNumber: 1 })
    const q2 = ratingApiSlice.useGetStudentRatingQuery({ ...base, weekNumber: 2 })
    const q3 = ratingApiSlice.useGetStudentRatingQuery({ ...base, weekNumber: 3 })
    const q4 = ratingApiSlice.useGetStudentRatingQuery({ ...base, weekNumber: 4 })
    const queries = [q1, q2, q3, q4]

    if (queries.some((q) => q.isLoading)) {
        return (
            <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress size={24} sx={{ color: ui.pink }} />
            </Box>
        )
    }

    const byStudent = new Map<number, { student: StudentRatingState; weeks: Record<number, WeekCell> }>()
    queries.forEach((q, idx) => {
        const week = WEEKS[idx]
        ;(q.data?.data || []).forEach((student) => {
            let entry = byStudent.get(student.userSubjectEnrollId)
            if (!entry) {
                entry = { student, weeks: {} }
                byStudent.set(student.userSubjectEnrollId, entry)
            }
            entry.weeks[week] = {
                p1: student.practiceStatus || '',
                p2: student.practice2Status || '',
                point: student.practicePoint || 0,
            }
        })
    })

    const rows = Array.from(byStudent.values())

    return (
        <TableContainer component={Paper} sx={tableStyles.container} elevation={0}>
            <Table>
                <TableHead sx={tableStyles.head}>
                    <TableRow>
                        <TableCell>{formatMessage({ id: 'student' })}</TableCell>
                        {WEEKS.map((w) => (
                            <TableCell key={w} align="center">
                                {w} {formatMessage({ id: 'week' })}
                                <Box sx={{ fontSize: '11px', color: ui.muted }}>П1 · П2</Box>
                            </TableCell>
                        ))}
                        <TableCell align="right">{formatMessage({ id: 'monthTotal' })}</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map(({ student, weeks }) => {
                        let monthPoint = 0
                        let attended = 0
                        WEEKS.forEach((w) => {
                            const cell = weeks[w]
                            if (!cell) return
                            monthPoint += cell.point
                            if (PRESENT.has(cell.p1)) attended += 1
                            if (PRESENT.has(cell.p2)) attended += 1
                        })
                        return (
                            <TableRow key={student.userSubjectEnrollId} sx={tableStyles.row}>
                                <TableCell>
                                    {student.firstName} {student.lastName}
                                    <Box sx={{ fontSize: '12px', color: ui.muted }}>{student.groupTitle}</Box>
                                </TableCell>

                                {WEEKS.map((w) => {
                                    const cell = weeks[w]
                                    return (
                                        <TableCell key={w} align="center">
                                            <Box sx={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                                <Badge status={cell?.p1} />
                                                <Badge status={cell?.p2} />
                                            </Box>
                                            <Box sx={{ fontSize: '12px', color: ui.muted, mt: '4px' }}>
                                                {cell?.point ?? 0} / {WEEK_MAX}
                                            </Box>
                                        </TableCell>
                                    )
                                })}

                                <TableCell align="right">
                                    <Box sx={{ fontWeight: 700 }}>{monthPoint}</Box>
                                    <Box sx={{ fontSize: '12px', color: ui.muted }}>
                                        / {MONTH_MAX} · {attended}/{TOTAL_SLOTS} {formatMessage({ id: 'attended' })}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>

            <Box sx={{ p: 2, display: 'flex', gap: 3, fontSize: '12px', color: ui.muted, flexWrap: 'wrap' }}>
                <span><Badge status="WITH_CAMERA" /> {formatMessage({ id: 'withCamera' })}</span>
                <span><Badge status="WITHOUT_CAMERA" /> {formatMessage({ id: 'withoutCamera' })}</span>
                <span><Badge status="ABSENT" /> {formatMessage({ id: 'absent' })}</span>
                <span><Badge status="" /> {formatMessage({ id: 'notMarked' })}</span>
            </Box>
        </TableContainer>
    )
}

export default AllWeeksSummary
